<template>
  <TheMainContent>
    <RevContainer class="mb-16 mt-32" data-spec="body">
      <div data-spec="main">
        <div
          :data-cnstrc-num-results="productsSearchData.total"
          :data-cnstrc-result-page="page"
          data-cnstrc-search
          :data-cnstrc-search-term="query"
          data-test="product-results-refinement"
        >
          <div class="mb-12 justify-between lg:mb-32 lg:flex">
            <h1 class="heading-1">
              {{ i18n(translations.recapTitle) }} <span>{{ query }}</span>
            </h1>
          </div>
          <div class="sticky -top-1 z-[1]">
            <TopFilters
              v-model:filters="filters"
              v-model:price="price"
              v-model:sort="sort"
              :facets
              :is-pending
              :price-facet
              :search-nb-results="productsSearchData.total"
              :sort-options
            />
          </div>
          <div class="mt-16 lg:flex lg:gap-16">
            <div class="w-full">
              <ResultsCount
                class="md:mb-16"
                :is-pending
                :total="productsSearchData.total"
              />

              <SwapPill
                v-if="isBuybackEnabled"
                :class="{ 'lg:hidden': !showPlpBlocks }"
              />

              <NoResults
                v-if="productsSearchData.products.length === 0"
                class="my-32"
                data-spec="body"
              />
              <ResultGrid
                v-else
                class="mt-8 md:mt-32"
                :is-filtered
                :is-pending
                list="search_results"
                :page-input="page"
                :plp-blocks
                :products="productsSearchData.products"
                with-payment-advertisement
              />
            </div>
          </div>

          <Pagination
            v-model="page"
            :page-count="productsSearchData.pageCount"
          />
        </div>
      </div>
    </RevContainer>
    <FullscreenFilters
      v-model:filters="filters"
      v-model:price="price"
      v-model:sort="sort"
      :facets
      :price-facet
      :search-nb-results="productsSearchData.total"
      :sort-options
    />
  </TheMainContent>
</template>

<script lang="ts" setup>
import { createError, useHead, useRuntimeConfig } from '#imports'
import { computed, onMounted, ref, watch } from 'vue'

import { useBuybackEnabled } from '@backmarket/nuxt-layer-buyback/app/composables/useBuybackEnabled/useBuybackEnabled'
import FullscreenFilters from '@backmarket/nuxt-layer-recommendation/filtering/FullscreenFilters.vue'
import TopFilters from '@backmarket/nuxt-layer-recommendation/filtering/TopFilters.vue'
import type {
  Facet,
  UiPriceFacet,
} from '@backmarket/nuxt-layer-recommendation/filtering/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useVisitorId } from '@backmarket/nuxt-module-identification/useVisitorId'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevContainer } from '@ds/components/Container'

import TheMainContent from '~/components/TheMainContent/TheMainContent.vue'
import SwapPill from '~/scopes/buyback/swap/components/SwapPill/SwapPill.vue'
import {
  getSortOptions,
  useAlgoliaConfiguration,
} from '~/scopes/product-list/helpers/helpers'

import NoResults from '../../components/NoResults/NoResults.vue'
import Pagination from '../../components/Pagination/Pagination.vue'
import ResultGrid from '../../components/ResultGrid/ResultGrid.vue'
import ResultsCount from '../../components/ResultsCount/ResultsCount.vue'
import { useFiltersFromUrl } from '../../composables/useFiltersFromUrl'
import { usePlpBlocks } from '../../composables/usePlpBlocks/usePlpBlocks'
import { useProductsSearch } from '../../composables/useProductsSearch/useProductsSearch'
import { useSearchState } from '../../composables/useSearchState/useSearchState'

import translations from './SearchResultPage.translations'

const i18n = useI18n()
const { isBuybackEnabled } = useBuybackEnabled()

const { data: searchConfig } = await useAlgoliaConfiguration('search-landing')

if (searchConfig.value === undefined) {
  throw createError({
    status: 500,
    statusText: 'Unable to retrieve search configuration',
    fatal: true,
  })
}

const sortOptions = getSortOptions(searchConfig.value.indexes)

const {
  query,
  filters,
  sort,
  price,
  page,
  reset: resetSearchInputs,
  isResetting,
} = useSearchState(searchConfig.value)

const priceFacet = ref<UiPriceFacet | undefined>()
const facets = ref<Facet[]>([])

const { plpBlocks } = usePlpBlocks(() => undefined)
const isFiltered = computed(
  () =>
    Object.values(filters).some((values) => values.length > 0) ||
    price.value !== undefined,
)

const showPlpBlocks = computed(() => Boolean(plpBlocks.value))

const { trackViewSearchResults } = useTracking()
const visitorId = useVisitorId()

const { data: productsSearchData, refresh: refreshProductsSearch } =
  await useProductsSearch(
    '',
    searchConfig.value,
    {
      query,
      filtersInput: filters,
      priceInput: price,
      sortInput: sort,
      pageInput: page,
      userToken: visitorId,
    },
    { paused: isResetting },
  )

// @TODO: Replace this with a real loading state
const isPending = ref(false)

function setFacetsAndPriceFacet() {
  facets.value = productsSearchData.value.facets
  priceFacet.value = productsSearchData.value.priceFacet
}

setFacetsAndPriceFacet()
watch(query, async (searchTerm) => {
  resetSearchInputs()
  await refreshProductsSearch()
  trackViewSearchResults({ searchTerm })
  setFacetsAndPriceFacet()
})

useFiltersFromUrl(filters, price, page)

const {
  public: { COMPANY_NAME },
} = useRuntimeConfig()
useHead({
  meta: [
    {
      name: 'robots',
      content: 'noindex',
    },
  ],
  title: query,
  titleTemplate: `%s | ${COMPANY_NAME}`,
})

onMounted(() => {
  trackViewSearchResults({ searchTerm: query.value })
})
</script>
